import React, { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ResponsiveImg from "./ResponsiveImg";

const SLIDES = [
  { url: "/viewlux/gallery/g1.jpg", caption: "Living · Picture Window" },
  { url: "/viewlux/gallery/g9.jpg", caption: "French Doors · Picture Windows" },
  { url: "/viewlux/gallery/g2.jpg", caption: "Kitchen · Sliding Window" },
  { url: "/viewlux/gallery/g3.jpg", caption: "Corner · Specialty Glass" },
  { url: "/viewlux/gallery/g4.jpg", caption: "Panoramic · Sliding Doors" },
  { url: "/viewlux/gallery/g5.jpg", caption: "VLX · 150 Heavy-Duty" },
  { url: "/viewlux/gallery/g6.jpg", caption: "VLX · 138 Panoramic" },
  { url: "/viewlux/gallery/g7.jpg", caption: "Single Swing Door" },
  { url: "/viewlux/gallery/g8.jpg", caption: "Bi-Folding · Garden" },
  { url: "/viewlux/hero/living-forest.jpg", caption: "Living · Forest View" },
  { url: "/viewlux/hero/about.jpg", caption: "Architecture · Studio" },
  { url: "/viewlux/hero/bifold.jpg", caption: "Bi-Folding · Patio" },
  { url: "/viewlux/hero/exterior-glass.jpg", caption: "Exterior · Glass Wall" },
];

export default function Gallery() {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const scrollToIndex = useCallback((idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const slide = el.children[idx];
    if (!slide) return;
    el.scrollTo({ left: slide.offsetLeft - el.offsetLeft, behavior: "smooth" });
  }, []);

  const next = useCallback(() => {
    setActive((a) => (a + 1) % SLIDES.length);
  }, []);

  const prev = useCallback(() => {
    setActive((a) => (a - 1 + SLIDES.length) % SLIDES.length);
  }, []);

  // Keep active updated while user manually scrolls
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = el.scrollLeft + el.clientWidth / 2;
        let bestIdx = 0;
        let bestDist = Infinity;
        for (let i = 0; i < el.children.length; i++) {
          const c = el.children[i];
          const cCenter = c.offsetLeft + c.clientWidth / 2;
          const d = Math.abs(cCenter - center);
          if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
          }
        }
        setActive(bestIdx);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Sync active state to scroll position
  useEffect(() => {
    scrollToIndex(active);
  }, [active, scrollToIndex]);

  // Auto-advance
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-[#0a1a17]"
    >
      <div className="container-vlx pt-24 md:pt-32 pb-10 md:pb-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
          <div>
            <span className="eyebrow eyebrow-light">Projects &amp; Moments</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-white text-[36px] md:text-[60px] leading-[1.05]">
              Where craft meets{" "}
              <em className="italic font-light text-[#e2b880]">daylight.</em>
            </h2>
          </div>
          <p className="text-white/60 max-w-md font-nunito leading-relaxed text-[14.5px]">
            A selection of ViewLux systems installed across residential and
            commercial projects — each a study in sightlines, proportion, and
            quiet engineering.
          </p>
        </div>
      </div>

      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div
          ref={scrollerRef}
          data-testid="gallery-carousel"
          className="rail flex overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-thin gap-4 md:gap-6 px-6 md:px-12 pb-2"
          style={{ scrollbarWidth: "thin" }}
        >
          {SLIDES.map((s, i) => (
            <figure
              key={s.url + i}
              data-testid={`gallery-slide-${i}`}
              className="snap-center shrink-0 relative overflow-hidden bg-[#0f2420]"
              style={{ width: "min(85vw, 1100px)" }}
            >
              <div className="aspect-[16/9] relative">
                <ResponsiveImg
                  src={s.url}
                  alt={s.caption}
                  sizes="85vw"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[3000ms] ease-out hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a17]/65 via-transparent to-transparent" />
                <figcaption className="absolute bottom-5 left-6 right-6 flex items-end justify-between gap-4">
                  <span className="text-white text-[10px] tracking-[0.4em] uppercase font-raleway">
                    {s.caption}
                  </span>
                  <span className="text-[#e2b880] text-[10px] tracking-[0.4em] uppercase font-raleway">
                    VLX / {String(i + 1).padStart(3, "0")}
                  </span>
                </figcaption>
              </div>
            </figure>
          ))}
        </div>

        {/* Arrows */}
        <button
          data-testid="gallery-prev"
          aria-label="Previous slide"
          onClick={prev}
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-[#0a1a17]/80 border border-[#c99658]/40 text-[#e2b880] hover:bg-[#c99658] hover:text-[#0a1a17] transition-colors backdrop-blur"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          data-testid="gallery-next"
          aria-label="Next slide"
          onClick={next}
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-[#0a1a17]/80 border border-[#c99658]/40 text-[#e2b880] hover:bg-[#c99658] hover:text-[#0a1a17] transition-colors backdrop-blur"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Indicators */}
      <div className="container-vlx pt-8 pb-20 md:pb-28 flex items-center justify-between gap-6">
        <div className="flex items-center gap-2 flex-wrap">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`gallery-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setActive(i)}
              className={`h-px transition-all duration-500 ${
                i === active
                  ? "w-10 bg-[#e2b880]"
                  : "w-5 bg-[#c99658]/30 hover:bg-[#c99658]/60"
              }`}
            />
          ))}
        </div>
        <div className="font-raleway text-[11px] tracking-[0.4em] uppercase text-[#e2b880]/80 tabular-nums">
          {String(active + 1).padStart(2, "0")}
          <span className="text-[#e2b880]/40 mx-2">/</span>
          {String(SLIDES.length).padStart(2, "0")}
        </div>
      </div>
    </section>
  );
}
